import { formatDate } from '../../utils/utils'

import { formatMoney, type CurrencyCode } from '@percent/utility'
import type {
  CandidateAttributeChange,
  CandidateEvaluationLeaf,
  CandidateEvaluationNode,
  CandidateHistoryChangeEntry,
  CandidateHistoryEmailEntry,
  CandidateHistoryEntry,
  CandidateHistoryStateChangeEntry
} from '@percent/partner-dashboard/services/grants/grantsService.types'

export type TimelineLine = {
  label: string
  value: string
}

export type TimelineChange = {
  label: string
  previous?: string
  next: string
}

type TimelineContent =
  | {
      kind: 'status'
      title: string
      from?: string
      to: string
      reason: string
      checks?: { summary: string; items: TimelineLine[] }
    }
  | { kind: 'change'; title: string; changes: TimelineChange[] }
  | { kind: 'email'; title: string; subject: string; recipient: string }

export type TimelineItem = TimelineContent & {
  id: string
  occurredAt: string
}

// Narrowed rather than `TFunction`, whose dynamic-key overload widens the return to include null.
// Same narrowing `formatRejectionReason` does.
type Translate = (key: string, options?: Record<string, unknown>) => string

const KEY = 'grants.candidateDetails.timeline'

/**
 * The state machine's own event vocabulary, owned by the grants module. A definition may reference
 * a type outside this list, so anything unrecognised falls back to `humaniseEventType`.
 */
const CANDIDATE_EVENT_TYPES = [
  'EVENT_NOMINATION_SUBMITTED',
  'EVENT_CANDIDATE_APPLICATION_SUBMITTED',
  'EVENT_CANDIDATE_ORGANISATION_ATTACHED',
  'EVENT_CANDIDATE_ORGANISATION_REJECTED',
  'EVENT_CANDIDATE_PASSED_VERIFICATION',
  'EVENT_CANDIDATE_FAILED_VERIFICATION',
  'EVENT_CANDIDATE_TASK_COMPLETED',
  'EVENT_CANDIDATE_NONPROFIT_CLAIM_APPROVED',
  'EVENT_CANDIDATE_NONPROFIT_CLAIM_REJECTED',
  'EVENT_CANDIDATE_ORG_CONTACT_SIGNED_UP',
  'EVENT_CANDIDATE_REJECTED_BY_ADMIN',
  'EVENT_CANDIDATE_DONATION_PAID',
  'EVENT_CANDIDATE_DONATION_PROCESSING',
  'EVENT_CANDIDATE_EMAIL_SENT',
  'EVENT_CANDIDATE_DUPLICATE_REJECTED',
  'EVENT_CANDIDATE_MERGE_REVIEW_REQUESTED',
  'EVENT_CANDIDATE_MERGE_REVIEW_COMPLETED',
  'EVENT_CANDIDATE_OPENED_TO_NONPROFIT',
  'EVENT_CANDIDATE_STATE_INCOMPLETE',
  'EVENT_CANDIDATE_SCHEDULED_EVENT'
] as const

/** Mirrors `candidateEventTypeToLabel` in the grants module. */
export const humaniseEventType = (eventType: string) =>
  eventType
    .replace(/^EVENT_CANDIDATE_/, '')
    .replace(/^EVENT_/, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

/** `orgContactEmail` -> `Org contact email`, for attributes we have no copy for. */
export const humaniseField = (field: string) => {
  const spaced = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase()

  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const isMoney = (value: unknown): value is { amount: number; currency: string } =>
  typeof value === 'object' && value !== null && 'amount' in value && 'currency' in value

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/

const formatAttributeValue = (value: unknown, locale: string, t: Translate): string => {
  if (value === null || value === undefined || value === '') return t('typography.notAvailable')

  if (isMoney(value)) {
    return formatMoney({ amount: Number(value.amount), currency: value.currency as CurrencyCode }, { locale })
  }

  if (Array.isArray(value)) return value.join(', ')

  if (typeof value === 'string' && ISO_DATE.test(value)) return formatDate(value)

  if (typeof value === 'object') return JSON.stringify(value)

  return String(value)
}

const flattenSatisfiedLeaves = (node: CandidateEvaluationNode): CandidateEvaluationLeaf[] => {
  if (!node.satisfied) return []

  return node.node === 'leaf' ? [node.leaf] : node.children.flatMap(flattenSatisfiedLeaves)
}

const asStringArray = (value: unknown): string[] => (Array.isArray(value) ? value.map(String) : [])

/** The state machine's comparison vocabulary, as partner-facing words. */
const OPERATORS: Record<string, string> = {
  '==': 'equals',
  '!=': 'notEquals',
  '>': 'greaterThan',
  '>=': 'atLeast',
  '<': 'lessThan',
  '<=': 'atMost',
  in: 'isOneOf',
  notIn: 'isNotOneOf',
  contains: 'contains',
  notContains: 'notContains'
}

const MAX_LIST_LENGTH = 80

const LIST_OPERATORS: Record<string, string> = { isOneOf: 'inList', isNotOneOf: 'notInList' }

type LeafContext = {
  locale: string
  /** `taskTemplateId` -> task name, from the program's own states. */
  taskNames: Record<string, string>
  t: Translate
}

/**
 * `{ field, operator, expected, actual }` as a sentence — the comparison the program was configured
 * with, and the candidate's own value where the leaf recorded one. A `time.now` field reports
 * `actualFrom` instead, since the clock it read is not stored per leaf. A list too long to read is
 * named, not spelled out.
 */
const comparison = (detail: Record<string, unknown>, field: string, { locale, t }: LeafContext) => {
  const operator = OPERATORS[String(detail.operator)]

  if (!operator) return null

  const expected = Array.isArray(detail.expected)
    ? detail.expected.map(value => formatAttributeValue(value, locale, t)).join(', ')
    : formatAttributeValue(detail.expected, locale, t)
  const listOperator = expected.length > MAX_LIST_LENGTH ? LIST_OPERATORS[operator] : undefined
  const template = listOperator ? `${KEY}.checks.inList` : `${KEY}.checks.comparison`
  const values = {
    field: humaniseField(field),
    operator: t(`${KEY}.checks.operators.${listOperator ?? operator}`),
    expected
  }

  return 'actual' in detail
    ? t(`${template}WithActual`, { ...values, actual: formatAttributeValue(detail.actual, locale, t) })
    : t(template, values)
}

const formatEventPresenceLeaf = (label: string, detail: Record<string, unknown>, t: Translate): TimelineLine => {
  const missing = asStringArray(detail.missing)

  return missing.length > 0
    ? { label, value: t(`${KEY}.checks.waitingFor`, { events: missing.map(humaniseEventType).join(', ') }) }
    : {
        label,
        value: t(`${KEY}.checks.eventsSeen`, { events: asStringArray(detail.seen).map(humaniseEventType).join(', ') })
      }
}

const formatEventLeaf = (detail: Record<string, unknown>, context: LeafContext): TimelineLine | null => {
  const { taskNames, t } = context

  if (detail.eventType === 'EVENT_CANDIDATE_EMAIL_SENT') return null

  // A task-driven condition names the task rather than the event constant behind it — the same
  // `taskTemplateId` NIL-4742 uses to name the task that failed a decision.
  const taskTemplateIds = asStringArray(detail.taskTemplateId)
  const label = t(taskTemplateIds.length > 0 ? `${KEY}.leafKinds.task` : `${KEY}.leafKinds.event`)
  const tasks = taskTemplateIds.map(id => taskNames[id]).filter(Boolean)
  const subject = tasks.length > 0 ? tasks.join(', ') : humaniseEventType(String(detail.eventType))

  if (typeof detail.requiredCopies === 'number' && detail.requiredCopies > 1) {
    return {
      label,
      value: t(`${KEY}.checks.copiesCompleted`, {
        subject,
        completed: Number(detail.distinctCompleted ?? 0),
        required: detail.requiredCopies
      })
    }
  }

  const conditions = Array.isArray(detail.conditions)
    ? detail.conditions
        .map(condition => comparison(condition as Record<string, unknown>, String(condition.field), context))
        .filter(Boolean)
    : []

  if (conditions.length > 0) {
    return { label, value: t(`${KEY}.checks.eventWhere`, { subject, conditions: conditions.join(', ') }) }
  }

  return {
    label,
    value: t(detail.seen === false ? `${KEY}.checks.waitingFor` : `${KEY}.checks.eventsSeen`, { events: subject })
  }
}

const COMPARISON_LEAF_KINDS = new Set(['organisation', 'field', 'payloadCondition'])

/**
 * The state machine's `label` is written for engineers (`EVENT_CANDIDATE_TASK_COMPLETED matching 1
 * payload condition(s)`), so it is never shown. A leaf is rendered from its structured `detail` or
 * not at all.
 */
const formatLeaf = (leaf: CandidateEvaluationLeaf, context: LeafContext): TimelineLine | null => {
  const label = context.t(`${KEY}.leafKinds.${leaf.kind}`)
  const detail = leaf.detail ?? {}

  if (leaf.kind === 'eventPresence') return formatEventPresenceLeaf(label, detail, context.t)

  if (leaf.kind === 'event') return formatEventLeaf(detail, context)

  const field = leaf.kind === 'payloadCondition' ? detail.key : detail.field

  if (!COMPARISON_LEAF_KINDS.has(leaf.kind) || typeof field !== 'string') return null

  const operator = detail.isOneOf === false ? 'notIn' : 'in'
  const value = comparison(leaf.kind === 'organisation' ? { ...detail, operator } : detail, field, context)

  return value ? { label, value } : null
}

const formatChange = (change: CandidateAttributeChange, locale: string, t: Translate): TimelineChange => {
  const label = t(`${KEY}.fields.${change.field}`, { defaultValue: humaniseField(change.field) })
  const next = formatAttributeValue(change.newValue, locale, t)

  if (change.previousValue === null || change.previousValue === undefined) return { label, next }

  return { label, previous: formatAttributeValue(change.previousValue, locale, t), next }
}

const stateChangeItem = (
  entry: CandidateHistoryStateChangeEntry,
  stateNames: Record<string, string>,
  context: LeafContext
): TimelineContent => {
  const { t } = context
  const stateName = (stateId: string) => stateNames[stateId] ?? stateId
  const { triggeringEventType, reason } = entry

  const item: TimelineContent = {
    kind: 'status',
    title: t(`${KEY}.statusChanged`),
    from: entry.fromStateId ? stateName(entry.fromStateId) : undefined,
    to: stateName(entry.toStateId),
    reason: (CANDIDATE_EVENT_TYPES as readonly string[]).includes(triggeringEventType)
      ? t(`${KEY}.reasons.${triggeringEventType}`)
      : humaniseEventType(triggeringEventType)
  }

  if (reason.type !== 'transition') return item

  const lines = flattenSatisfiedLeaves(reason.chosen.evaluation).flatMap(leaf => formatLeaf(leaf, context) ?? [])
  const items = lines.filter(
    (line, index) => lines.findIndex(other => other.label === line.label && other.value === line.value) === index
  )

  return items.length > 0 ? { ...item, checks: { summary: t(`${KEY}.checks.summary`), items } } : item
}

const changeItem = (entry: CandidateHistoryChangeEntry, locale: string, t: Translate): TimelineContent => {
  // Creation and deletion are recorded as one legible entry rather than one row per column, so they
  // stay a single line here too.
  if (entry.kind === 'created') return { kind: 'change', title: t(`${KEY}.candidateCreated`), changes: [] }

  if (entry.kind === 'deleted') return { kind: 'change', title: t(`${KEY}.candidateDeleted`), changes: [] }

  const changes = entry.changes.map(change => formatChange(change, locale, t))

  if (entry.mergedFromCandidateId) return { kind: 'change', title: t(`${KEY}.candidateMerged`), changes }

  return {
    kind: 'change',
    title: changes.length === 1 ? t(`${KEY}.fieldUpdated`, { field: changes[0].label }) : t(`${KEY}.candidateUpdated`),
    changes
  }
}

const emailItem = (entry: CandidateHistoryEmailEntry, t: Translate): TimelineContent => ({
  kind: 'email',
  title: t(`${KEY}.emailSent`),
  subject: entry.subject ?? humaniseField(entry.template),
  recipient: t(`${KEY}.email.recipientTypes.${entry.recipientType}`)
})

const toItem = (
  entry: CandidateHistoryEntry,
  stateNames: Record<string, string>,
  context: LeafContext
): TimelineContent | null => {
  switch (entry.kind) {
    case 'state_change':
      return stateChangeItem(entry, stateNames, context)
    case 'email_sent':
      return emailItem(entry, context.t)
    case 'created':
    case 'field_change':
    case 'deleted':
      return changeItem(entry, context.locale, context.t)
    default:
      return null
  }
}

export type MapHistoryParams = {
  entries: CandidateHistoryEntry[]
  stateNames: Record<string, string>
  taskNames?: Record<string, string>
  locale: string
  t: Translate
}

export const mapHistoryToTimeline = ({
  entries,
  stateNames,
  taskNames = {},
  locale,
  t
}: MapHistoryParams): TimelineItem[] => {
  const context: LeafContext = { locale, taskNames, t }

  return entries.flatMap((entry, index) => {
    const item = toItem(entry, stateNames, context)

    return item ? [{ id: `${entry.kind}-${entry.occurredAt}-${index}`, occurredAt: entry.occurredAt, ...item }] : []
  })
}
